import { ElementRef, Injectable } from "@angular/core";
import { ParamMap, Router } from "@angular/router";
import { MapPoint } from "@models/map/map-point";
import { PopupValue } from "@models/map/popup-value";
import { UrlMapProps } from "@models/map/url-map-props";
import { Logger } from "@services/logging.service";
import { WebAppService } from "@services/xhr/web-app-svc";
import Color from "esri/Color";
import { Multipoint, Point, Polyline } from "esri/geometry";
import Graphic from "esri/Graphic";
import IdentityManager from "esri/identity/IdentityManager";
import OAuthInfo from "esri/identity/OAuthInfo";
import GraphicsLayer from "esri/layers/GraphicsLayer";
import EsriMap from "esri/Map";
import Portal from "esri/portal/Portal";
import { SimpleLineSymbol, SimpleMarkerSymbol } from "esri/symbols";
import MapView from "esri/views/MapView";
import { BehaviorSubject, ReplaySubject, Subscription, throwError } from "rxjs";
import { catchError, filter, map } from "rxjs/operators";
import { mapCreds } from "src/environments/environment";
import { LAYERS } from "./map.layers";
import { Widget, WidgetService } from "./widget.service";
import esri = __esri;

export type ChangeOptions =
  | "zoom"
  | "zoomToPoints"
  | "basemap"
  | "enableInteraction"
  | "showLayers";
@Injectable({
  providedIn: "root",
})
export class MapService {
  isMobile = false;
  graphicsLayer: GraphicsLayer = new GraphicsLayer({
    maxScale: 0,
    minScale: 0,
  });
  graphicSelected$: BehaviorSubject<Graphic | null> =
    new BehaviorSubject<Graphic | null>(null);
  mapPoints$: BehaviorSubject<MapPoint[] | null> = new BehaviorSubject<
    MapPoint[] | null
  >(null);
  graphics: Graphic[] | undefined;
  urlMapProps: UrlMapProps;
  changes$: ReplaySubject<{ key: ChangeOptions; value: any } | null> =
    new ReplaySubject<{ key: ChangeOptions; value: any } | null>();
  subs: Subscription[] = [];
  view: MapView;
  map: EsriMap;
  loadComcastLayers: boolean = false;

  constructor(
    private widgetService: WidgetService,
    private router: Router,
    private webAppService: WebAppService
  ) {
    this.isMobile = this.checkIfMobile();
  }

  esriLogin(): Promise<void> {
    Logger.info("esri login");
    const CLIENT_ID = mapCreds.clientId;
    const portalUrl = mapCreds.portalUrl;

    const currentRoute = window.location.hash;
    Logger.info("setting appRoute");
    localStorage.setItem("appRoute", `${currentRoute}`);

    Logger.info("creating info");
    const portal = new Portal({
      url: portalUrl,
    });
    portal.authMode = "immediate";

    const info = new OAuthInfo({
      appId: CLIENT_ID,
      portalUrl: portalUrl,
      popup: false,
    });

    Logger.info("registering info");
    IdentityManager.registerOAuthInfos([info]);
    Logger.info("calling getCredential");
    return IdentityManager.getCredential(portalUrl + "/sharing/rest", {
      oAuthPopupConfirmation: false,
    }).then((credential) => {
      Logger.info("got credential");
      const { token, expires } = credential;
      localStorage.setItem("xmEsriToken", token);
      localStorage.setItem("xmEsriTokenExpires", "" + expires);
    });
  }

  /**
   * When component is created, it will get UrlMapProps from the url params,
   * and save them into this.initialUrlMapParams
   */
  url2MapProps(queryParamMap: ParamMap): UrlMapProps {
    const basemap = queryParamMap.has("basemap")
      ? encodeURI(queryParamMap.get("basemap") as string)
      : null;
    const zoom = queryParamMap.has("zoom")
      ? Number(queryParamMap.get("zoom"))
      : null;
    let point: Point | undefined;
    if (queryParamMap.get("latitude") && queryParamMap.get("longitude")) {
      point = new Point({
        latitude: Number(queryParamMap.get("latitude")),
        longitude: Number(queryParamMap.get("longitude")),
      });
    }

    return new UrlMapProps(basemap, zoom, point);
  }

  setMapPoints(mapPoints: MapPoint[]) {
    this.mapPoints$.next(mapPoints);
  }

  setZoom(zoom: number) {
    this.changes$.next({ key: "zoom", value: zoom });
  }

  zoomToPoints(mapPoints: MapPoint[]) {
    this.changes$.next({ key: "zoomToPoints", value: mapPoints });
  }

  setBasemap(basemapString: string) {
    this.changes$.next({ key: "basemap", value: basemapString });
  }

  enableInteraction(canInteract: boolean) {
    this.changes$.next({ key: "enableInteraction", value: canInteract });
  }

  showLayers(showLayers: boolean) {
    this.changes$.next({ key: "showLayers", value: showLayers });
  }

  createLine(mapPoint1: MapPoint, mapPoint2: MapPoint): Graphic {
    const newPath = [];
    newPath.push(
      [mapPoint1.longitude, mapPoint1.latitude],
      [mapPoint2.longitude, mapPoint2.latitude]
    );

    const polyline: Polyline = new Polyline({
      hasZ: false,
      hasM: true,
      paths: [newPath],
      spatialReference: { wkid: 4326 },
    });

    const lineSymbol: Partial<SimpleLineSymbol> = {
      type: "simple-line", // autocasts as new SimpleLineSymbol()
      color: Color.fromArray([0, 0, 0, 0.75]), // RGBA color values as an array
      width: 1,
      style: "short-dot",
    };
    return new Graphic({ symbol: lineSymbol, geometry: polyline });
  }

  /**
   * Initialize the EsriMap and the MapView.
   * After that it subscribes to any changes this.mapPoints$ and paints them to the map.
   * If there are any urlMapProps, the function will zoom to display those points.
   *
   * @param mapViewEl: ElementRef
   * @param urlMapProps: UrlMapProps
   * @returns void
   */
  initialize(
    mapViewEl: ElementRef,
    urlMapProps: UrlMapProps,
    widgets?: Widget[]
  ): void {
    Logger.info("initialize shared map");
    // Make an API call to Comcast GIS server to see if we have connectivity/ connected to VPN
    this.webAppService
      .getEsriLayer()
      .pipe(
        map(async (data) => {
          Logger.info("We have connectivity to Comcast internal GIS server");
          const tokenExpires = Number(
            localStorage.getItem("xmEsriTokenExpires")
          );
          const currentTime = new Date().getTime();
          if (isNaN(tokenExpires) || currentTime > tokenExpires) {
            Logger.info("found expired GIS token - login");
            await this.esriLogin();
          }
          Logger.info("found valid GIS token - initialize map");
          this.loadComcastLayers = true;
          this.initializePhase2(mapViewEl, urlMapProps, widgets);
        }),
        catchError((err) => {
          Logger.info("Failed to connect to Comcast internal GIS server");
          this.loadComcastLayers = false;
          this.initializePhase2(mapViewEl, urlMapProps, widgets);
          return throwError(err);
        })
      )
      .subscribe();
  }

  initializePhase2(
    mapViewEl: ElementRef,
    urlMapProps: UrlMapProps,
    widgets?: Widget[]
  ): void {
    const xmEsriToken = "" + localStorage.getItem("xmEsriToken");

    // PROD GIS
    let ESRI_BASE_URL =
      "https://aegis-enterprise.cable.comcast.com/server/rest/services";

    // set the token for esri authentication
    Logger.info("registering token properties");
    const tokenProperties = {
      server: ESRI_BASE_URL,
      token: xmEsriToken,
    };
    IdentityManager.registerToken(tokenProperties);
    Logger.info("initializing map");

    // Sets basemap in order of preference from the url, the saved user prefs, and a default (topo)
    this.map = this.initializeMap();
    this.view = this.initializeView(this.map, mapViewEl);
    this.widgetService.initializeWidgets(this.view, widgets);

    // track changes
    this.subs.push(this.trackChanges(this.map, this.view));
    this.subs.push(
      this.mapPoints$.subscribe(
        (mapPoints: MapPoint[] | null) =>
          (this.graphics = this.processMapPoints(mapPoints))
      )
    );
  }

  /**
   * Configures the EsriMap for the first time
   * @param urlMapProps: UrlMapProps | undefined
   * @returns EsriMap
   */
  initializeMap(): EsriMap {
    const basemap = localStorage.getItem("lastBasemap") || "topo-vector";

    const newMap = new EsriMap({
      basemap,
    });

    if (this.loadComcastLayers == true) {
      newMap.addMany([...LAYERS, this.graphicsLayer]);
    } else {
      newMap.addMany([this.graphicsLayer]);
    }

    return newMap;
  }

  /**
   * Configures the view any time we want to refresh or create a new MapView.
   * It will priorize UrlMapProps on top of previous values, and previous values on top of default values
   * for zoom and center
   * After view is created, it will add watchers for UrlMapProps and for click on the points.
   *
   * @param esriMap: EsriMap
   * @param mapViewEl: ElementRef
   * @param urlMapProps: UrlMapProps | undefined
   * @returns MapView
   */
  initializeView(esriMap: EsriMap, mapViewEl: ElementRef): MapView {
    // Initialize the MapView
    const mapViewProperties = {
      container: mapViewEl.nativeElement,
      center: new Point({ latitude: 39.67833, longitude: -104.87584 }),
      zoom: 4,
      map: esriMap,
      declaredClass: "map-ui",
      navigation: {
        mouseWheelZoomEnabled: false,
      },
    };

    const view = new MapView(mapViewProperties);
    // this is being set to work an issue where primary left click was not working, right click was.  If this is removed,
    // make sure you test that you can left click on a map icon and get the popup data window
    view.popup.autoOpenEnabled = false;

    return view;
  }

  configView(urlMapProps?: UrlMapProps) {
    let mapViewProperties: esri.MapViewProperties = {};

    if (this.view) {
      const previousView = this.view;
      mapViewProperties.center = previousView.center;
      mapViewProperties.zoom = previousView.zoom;
    }

    if (urlMapProps) {
      // check for previous state we want to bring to new view
      if (urlMapProps.zoom) {
        mapViewProperties.zoom = urlMapProps.zoom;
      }

      if (urlMapProps.center) {
        mapViewProperties.center = urlMapProps.center;
      }
    }

    this.addGraphicsLayerListener(this.view);
  }

  trackChanges(esriMap: EsriMap, view: MapView) {
    return this.changes$
      .pipe(filter((change: any) => !!change))
      .subscribe((change: { key: string; value: any } | null) => {
        if (change) {
          switch (change.key) {
            case "zoom":
              view.zoom = change.value;
              break;
            case "zoomToPoints":
              const points = change.value.map((mapPoint: MapPoint) => [
                mapPoint.longitude,
                mapPoint.latitude,
              ]);
              const multiPoint = new Multipoint({ points });
              if (!multiPoint.extent.height || !multiPoint.extent.width) {
                view.zoom = 16;
              }

              view
                .goTo(multiPoint.extent.expand(1.1), { animate: false })
                .catch((error: { name: string }) => {
                  if (error.name !== "AbortError") {
                    // Manually handle abort error for now as it fires every view change
                  }
                });

              break;
            case "basemap":
              const basemap = this.widgetService.basemapTitleMapper(
                change.value
              );
              esriMap.set("basemap", basemap);
              break;
            case "enableInteraction":
              view.navigation.browserTouchPanEnabled = change.value;
              break;
            case "showLayers":
              if (change.value) {
                esriMap.remove(this.graphicsLayer);
                if (this.loadComcastLayers == true) {
                  esriMap.addMany([...LAYERS, this.graphicsLayer]);
                } else {
                  esriMap.addMany([this.graphicsLayer]);
                }
              } else {
                esriMap.removeMany(LAYERS);
              }
              break;
          }
        }
      });
  }

  processMapPoints(mapPoints: MapPoint[] | null) {
    if (mapPoints) {
      this.graphicsLayer.removeAll();
      this.view.popup.close();
      const graphics = this.createSymbolGraphics(mapPoints);
      this.graphicsLayer.addMany(graphics);
      return graphics;
    }
  }

  /**
   * Opens pop up on the selected graphic
   * @param id: string
   * @returns void
   */
  openGraphicPopup(id: string): void {
    if (this.graphics) {
      const selectedGraphic = this.graphics.find((graphic) => {
        return graphic.attributes.id === id;
      });
      if (selectedGraphic) {
        this.view.popup.close();
        this.view.popup.open({
          location: selectedGraphic.geometry,
          content: this.templateBuilder(selectedGraphic.attributes.popupValues),
        });
        this.view.goTo(selectedGraphic);
        this.graphicSelected$.next(selectedGraphic);
      }
    }
  }

  /**
   * Add listener to click event, checks if there is a graphic in the location
   * and then opens a popup. It also sets this.graphicSelected$ with the selected
   * graphic.
   *
   * @param view: MapView
   * @returns void
   */
  addGraphicsLayerListener(view: MapView): void {
    view.on("click", (screenPoint: { mapPoint: any }) => {
      // @ts-ignore
      view.hitTest(screenPoint).then((response: { results: any[] }) => {
        if (response.results.length) {
          const graphic =
            response.results.find((result: { graphic: { layer: any } }) => {
              // check if the graphic belongs to the layer of interest
              return result.graphic.layer === this.graphicsLayer;
            })?.graphic || null;
          // do something with the result graphic
          if (graphic && graphic.attributes) {
            view.popup.open({
              content: this.templateBuilder(graphic.attributes.popupValues),
              location: screenPoint.mapPoint,
            });
          }
          this.graphicSelected$.next(graphic);
        }
      });
    });
  }

  /**
   * Programmaticaly builds HTML template from GraphicsAttributes.popupValues
   * @param popupValues: TODO Make this Map
   * @returns string
   */
  templateBuilder(popupValues: { [key: string]: PopupValue }): string {
    let html = ``;
    for (const prop in popupValues) {
      if (Object.prototype.hasOwnProperty.call(popupValues, prop)) {
        const attribute = popupValues[prop];
        html = html.concat(`<b>${attribute.title}:</b> ${attribute.value}<br>`);
      }
    }
    return html;
  }

  /**
   * Creates a graphic into a location.
   *
   * @param mapPoint: MapPoint
   * @returns Graphic
   */
  createSymbolGraphics(mapPoints: MapPoint[]): Graphic[] {
    return mapPoints.reduce(
      (graphicArr: Graphic[], mapPoint: MapPoint, index: number) => {
        if (mapPoint.longitude && mapPoint.latitude) {
          const point = new Point({
            x: mapPoint.longitude,
            y: mapPoint.latitude,
          });
          if (mapPoint.connectPoints && index !== 0) {
            graphicArr.push(
              this.createLine(mapPoints[index - 1], mapPoints[index])
            );
          }
          const symbol = new SimpleMarkerSymbol(mapPoint.symbolProperties);
          graphicArr.push(
            new Graphic({
              geometry: point,
              symbol,
              attributes: mapPoint.graphicAttributes,
            })
          );
        }
        return graphicArr;
      },
      new Array<Graphic>()
    );
  }

  addGraphic(graphic: Graphic) {
    this.graphicsLayer.add(graphic);
  }

  goTo(target: Graphic | any) {
    if (this.view) {
      this.view.goTo(target);
    }
  }

  checkIfMobile() {
    return typeof window.screen.orientation !== "undefined";
  }
}
